/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativosPublico, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaDelivery.module.scss";

// Datos IPSOS Delivery (Base: 400 casos / 311 usuarios)
const IPSOS_DATA = {
  usaDelivery: 0.769, // 76.9% usa apps de delivery
  noUsaDelivery: 0.231, // 23.1% no usa

  // Market share de apps (Base: 311 usuarios)
  // Nota: Cornershop (7.4%) fue adquirido por Uber Eats, se suma a su market share
  apps: {
    "Uber Eats": 0.66, // 58.6% + 7.4% Cornershop = 66%
    "Pedidos Ya": 0.264, // 26.4%
    Rappi: 0.15, // 15.0%
  },

  // Tipo de producto (Base: 311)
  tipoProducto: {
    comida: 0.903, // 90.3%
    supermercado: 0.097, // 9.7%
  },

  // Frecuencia de uso (Base: 311 usuarios)
  frecuenciaUso: {
    todosLosDias: { pct: 0.014, factor: 30 }, // 1.4% - 30 pedidos/mes
    finesDeSemana: { pct: 0.075, factor: 8 }, // 7.5% - 8 pedidos/mes
    variosDiasSemana: { pct: 0.067, factor: 12 }, // 6.7% - 12 pedidos/mes
    unaVezSemana: { pct: 0.273, factor: 4 }, // 27.3% - 4 pedidos/mes
    cadaDosSemananas: { pct: 0.297, factor: 2 }, // 29.7% - 2 pedidos/mes
    cadaTresSemanas: { pct: 0.08, factor: 1.3 }, // 8.0% - 1.3 pedidos/mes
    unaVezMes: { pct: 0.18, factor: 1 }, // 18.0% - 1 pedido/mes
    menosDeUnaVezMes: { pct: 0.014, factor: 0.5 }, // 1.4% - 0.5 pedidos/mes
  },

  // Gasto promedio mensual en delivery: $80,000 - $150,000
  gastoPromedioMensual: { min: 80000, max: 150000, promedio: 115000 },
};

// Apps disponibles para simulación
const APPS_SIMULABLES = Object.keys(IPSOS_DATA.apps);

// Opciones de ticket promedio (CLP) - basado en experiencia real del usuario
const TICKETS_PROMEDIO = [8000, 12000, 15000, 20000];

// Escenarios de captación
const ESCENARIOS_CAPTACION = [5, 10, 20];

export const Delivery = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [appSeleccionada, setAppSeleccionada] = useState("Uber Eats");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(10);
  const [ticketPromedio, setTicketPromedio] = useState(12000);
  const [verAnual, setVerAnual] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativosPublico({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // Calcular mercado con IPSOS
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Usuarios de delivery
    const usuariosDelivery = Math.round(totalEjecutivos * IPSOS_DATA.usaDelivery);
    const noUsuarios = Math.round(totalEjecutivos * IPSOS_DATA.noUsaDelivery);

    // Calcular pedidos mensuales según frecuencia
    const { frecuenciaUso, apps } = IPSOS_DATA;
    let pedidosMensuales = 0;

    Object.values(frecuenciaUso).forEach(({ pct, factor }) => {
      pedidosMensuales += Math.round(usuariosDelivery * pct * factor);
    });

    // Mercado mensual y anual
    const mercadoMensual = pedidosMensuales * ticketPromedio;
    const mercadoAnual = mercadoMensual * 12;

    // Desglose por frecuencia
    const desgloseFrecuencia = {
      altaFrecuencia: {
        // 1+ vez/semana: todos los días + fines de semana + varios días + 1 vez/semana
        pct:
          frecuenciaUso.todosLosDias.pct +
          frecuenciaUso.finesDeSemana.pct +
          frecuenciaUso.variosDiasSemana.pct +
          frecuenciaUso.unaVezSemana.pct,
        personas: Math.round(
          usuariosDelivery *
            (frecuenciaUso.todosLosDias.pct +
              frecuenciaUso.finesDeSemana.pct +
              frecuenciaUso.variosDiasSemana.pct +
              frecuenciaUso.unaVezSemana.pct)
        ),
        pedidos: Math.round(
          usuariosDelivery * frecuenciaUso.todosLosDias.pct * frecuenciaUso.todosLosDias.factor +
            usuariosDelivery * frecuenciaUso.finesDeSemana.pct * frecuenciaUso.finesDeSemana.factor +
            usuariosDelivery * frecuenciaUso.variosDiasSemana.pct * frecuenciaUso.variosDiasSemana.factor +
            usuariosDelivery * frecuenciaUso.unaVezSemana.pct * frecuenciaUso.unaVezSemana.factor
        ),
      },
      mediaFrecuencia: {
        // Cada 2-3 semanas
        pct: frecuenciaUso.cadaDosSemananas.pct + frecuenciaUso.cadaTresSemanas.pct,
        personas: Math.round(
          usuariosDelivery * (frecuenciaUso.cadaDosSemananas.pct + frecuenciaUso.cadaTresSemanas.pct)
        ),
        pedidos: Math.round(
          usuariosDelivery * frecuenciaUso.cadaDosSemananas.pct * frecuenciaUso.cadaDosSemananas.factor +
            usuariosDelivery * frecuenciaUso.cadaTresSemanas.pct * frecuenciaUso.cadaTresSemanas.factor
        ),
      },
      bajaFrecuencia: {
        // 1 vez/mes o menos
        pct: frecuenciaUso.unaVezMes.pct + frecuenciaUso.menosDeUnaVezMes.pct,
        personas: Math.round(usuariosDelivery * (frecuenciaUso.unaVezMes.pct + frecuenciaUso.menosDeUnaVezMes.pct)),
        pedidos: Math.round(
          usuariosDelivery * frecuenciaUso.unaVezMes.pct * frecuenciaUso.unaVezMes.factor +
            usuariosDelivery * frecuenciaUso.menosDeUnaVezMes.pct * frecuenciaUso.menosDeUnaVezMes.factor
        ),
      },
    };

    // Market share por app
    const marketShareApps = {};
    Object.entries(apps).forEach(([app, share]) => {
      marketShareApps[app] = {
        share,
        usuarios: Math.round(usuariosDelivery * share),
        pedidos: Math.round(pedidosMensuales * share),
        mercadoMensual: Math.round(mercadoMensual * share),
      };
    });

    return {
      usuariosDelivery,
      noUsuarios,
      pedidosMensuales,
      pedidosAnuales: pedidosMensuales * 12,
      mercadoMensual,
      mercadoAnual,
      desgloseFrecuencia,
      marketShareApps,
    };
  }, [totalEjecutivos, ticketPromedio]);

  if (loading) return <Loader />;

  return (
    <div className={styles.deliveryPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>DELIVERY</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.deliveryContent}>
        {/* Tabla integrada: uso + frecuencia */}
        {resultados && (
          <div className={styles.tablaResultadosContainer}>
            <table className={cx("table table-sm", styles.tablaResultados, styles.tablaIntegrada)}>
              <thead className="table-dark">
                <tr>
                  <th className="text-center">No usan delivery</th>
                  <th className="text-center">Sí usan delivery</th>
                  <th className="text-center">Frecuencia de uso</th>
                  <th className="text-center">Personas</th>
                  <th className="text-center">
                    <div className={styles.headerConsumos}>
                      <span>Pedidos</span>
                      <div className={styles.toggleContainer}>
                        <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                        <label className={styles.toggleSwitch}>
                          <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                          <span className={styles.toggleSlider} />
                        </label>
                        <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td rowSpan={3} className={cx("text-center align-middle", styles.celdaNoUsa)}>
                    <div className={styles.celdaContenido}>
                      <span className={styles.badgeIpsos}>{(IPSOS_DATA.noUsaDelivery * 100).toFixed(0)}%</span>
                      <div className={styles.valorGrande}>{formatNumber(resultados.noUsuarios)}</div>
                    </div>
                  </td>
                  <td rowSpan={3} className={cx("text-center align-middle", styles.celdaSiUsa)}>
                    <div className={styles.celdaContenido}>
                      <span className={styles.badgeIpsos}>{(IPSOS_DATA.usaDelivery * 100).toFixed(0)}%</span>
                      <div className={styles.valorGrande}>{formatNumber(resultados.usuariosDelivery)}</div>
                    </div>
                  </td>
                  <td className={styles.celdaFrecuencia}>
                    <span className={styles.badgeIpsos}>
                      {(resultados.desgloseFrecuencia.altaFrecuencia.pct * 100).toFixed(0)}%
                    </span>
                    <span className={styles.frecuenciaTexto}>Alta: 1 o más veces por semana</span>
                  </td>
                  <td className="text-center">{formatNumber(resultados.desgloseFrecuencia.altaFrecuencia.personas)}</td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.altaFrecuencia.pedidos * 12
                        : resultados.desgloseFrecuencia.altaFrecuencia.pedidos
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.celdaFrecuencia}>
                    <span className={styles.badgeIpsos}>
                      {(resultados.desgloseFrecuencia.mediaFrecuencia.pct * 100).toFixed(0)}%
                    </span>
                    <span className={styles.frecuenciaTexto}>Media: cada 2-3 semanas</span>
                  </td>
                  <td className="text-center">
                    {formatNumber(resultados.desgloseFrecuencia.mediaFrecuencia.personas)}
                  </td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.mediaFrecuencia.pedidos * 12
                        : resultados.desgloseFrecuencia.mediaFrecuencia.pedidos
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.celdaFrecuencia}>
                    <span className={styles.badgeIpsos}>
                      {(resultados.desgloseFrecuencia.bajaFrecuencia.pct * 100).toFixed(0)}%
                    </span>
                    <span className={styles.frecuenciaTexto}>Baja: 1 vez al mes o menos</span>
                  </td>
                  <td className="text-center">{formatNumber(resultados.desgloseFrecuencia.bajaFrecuencia.personas)}</td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.bajaFrecuencia.pedidos * 12
                        : resultados.desgloseFrecuencia.bajaFrecuencia.pedidos
                    )}
                  </td>
                </tr>
                <tr className={styles.filaTotalPedidos}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotalPedidos)}>
                    <strong>{formatNumber(verAnual ? resultados.pedidosAnuales : resultados.pedidosMensuales)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Ticket + Mercado */}
            <div className={styles.dosBloquesContainer}>
              {/* Ticket promedio */}
              <div className={styles.bloqueTicket}>
                <div className={styles.ticketLabel}>Ticket promedio</div>
                <div className={styles.ticketBox}>
                  <div className={styles.ticketOpciones}>
                    {TICKETS_PROMEDIO.map((t) => (
                      <label key={t} className={styles.ticketRadio}>
                        <input
                          type="radio"
                          name="ticketPromedio"
                          checked={ticketPromedio === t}
                          onChange={() => setTicketPromedio(t)}
                        />
                        <span>{formatLocal(convertCLP(t))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mercado */}
              <div className={styles.bloqueMercado}>
                <div className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</div>
                <div className={styles.mercadoValorGrande}>
                  {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
                </div>
              </div>
            </div>

            {/* Market Share por App */}
            <div className={styles.marketShareContainer}>
              <h3 className={styles.marketShareTitulo}>Market Share por App</h3>
              <div className={styles.appsGrid}>
                {Object.entries(resultados.marketShareApps).map(([app, data]) => {
                  const colorClass =
                    app === "Uber Eats" ? styles.uberEats : app === "Pedidos Ya" ? styles.pedidosYa : styles.rappiCard;
                  return (
                    <div key={app} className={cx(styles.appCard, colorClass)}>
                      <div className={styles.appNombre}>{app}</div>
                      <div className={styles.appShare}>
                        <span className={styles.badgeIpsos}>{(data.share * 100).toFixed(0)}%</span>
                      </div>
                      <div className={styles.appMetricas}>
                        <div>
                          <span className={styles.metricaLabel}>Usuarios</span>
                          <span className={styles.metricaValor}>{formatNumber(data.usuarios)}</span>
                        </div>
                        <div>
                          <span className={styles.metricaLabel}>Pedidos/{verAnual ? "año" : "mes"}</span>
                          <span className={styles.metricaValor}>
                            {formatNumber(verAnual ? data.pedidos * 12 : data.pedidos)}
                          </span>
                        </div>
                        <div>
                          <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                          <span className={styles.metricaValor}>
                            {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. Perfil: 50.4% solteros, 24.3% vive solo,
                38.9% vive en pareja. Zona residencial: 16.2% Las Condes, 10.9% Santiago, 8.1% Ñuñoa, 7.7% La Florida
                (mayoría sector oriente). Gasto promedio mensual en delivery: {formatLocal(convertCLP(80000))} -{" "}
                {formatLocal(convertCLP(150000))}.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Delivery</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Selector de App */}
              <div className={styles.appSelector}>
                <label>App objetivo:</label>
                <select value={appSeleccionada} onChange={(e) => setAppSeleccionada(e.target.value)}>
                  {APPS_SIMULABLES.map((app) => (
                    <option key={app} value={app}>
                      {app}
                    </option>
                  ))}
                </select>
              </div>

              {/* Market share actual */}
              <div className={styles.marketShareActual}>
                <h4>Market Share Actual en Edificios</h4>
                <div className={styles.appsGrid}>
                  {Object.entries(IPSOS_DATA.apps).map(([app, share]) => (
                    <div
                      key={app}
                      className={cx(
                        styles.appItem,
                        app === appSeleccionada && styles.selected,
                        app !== appSeleccionada && styles.appBlur
                      )}
                    >
                      <span className={styles.appNombre}>{app}</span>
                      <span>{(share * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Oportunidades */}
              <div className={styles.oportunidadesLista}>
                <h4>Oportunidades para {appSeleccionada}</h4>
                <ul>
                  <li>
                    <strong>
                      {formatNumber(
                        resultados.usuariosDelivery - (resultados.marketShareApps[appSeleccionada]?.usuarios || 0)
                      )}
                    </strong>{" "}
                    usuarios usan otras apps de delivery
                  </li>
                  <li>
                    <strong>
                      {formatLocal(
                        convertCLP(
                          resultados.mercadoMensual - (resultados.marketShareApps[appSeleccionada]?.mercadoMensual || 0)
                        )
                      )}
                    </strong>{" "}
                    de mercado mensual en competidores
                  </li>
                  <li>
                    <strong>{formatNumber(resultados.noUsuarios)}</strong> ejecutivos no usan delivery (potencial de
                    activación)
                  </li>
                </ul>
              </div>

              {/* Escenarios de captación */}
              <div className={styles.escenarios}>
                <h4>Escenarios de Captación</h4>
                {ESCENARIOS_CAPTACION.map((pct) => {
                  const usuariosOtras =
                    resultados.usuariosDelivery - (resultados.marketShareApps[appSeleccionada]?.usuarios || 0);
                  const captados = Math.round(usuariosOtras * (pct / 100));
                  const pedidosCaptados = Math.round(
                    (resultados.pedidosMensuales - (resultados.marketShareApps[appSeleccionada]?.pedidos || 0)) *
                      (pct / 100)
                  );
                  const mercadoGanado = pedidosCaptados * ticketPromedio;

                  return (
                    <div
                      key={pct}
                      className={cx(styles.escenarioItem, porcentajeCaptacion === pct && styles.escenarioActivo)}
                      onClick={() => setPorcentajeCaptacion(pct)}
                    >
                      <div className={styles.escenarioLabel}>Captar {pct}% de usuarios de otras apps</div>
                      <div className={styles.escenarioValores}>
                        <div>
                          <span className={styles.valorLabel}>Nuevos usuarios</span>
                          <span className={styles.valor}>{formatNumber(captados)}</span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Pedidos captados/mes</span>
                          <span className={styles.valor}>{formatNumber(pedidosCaptados)}</span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Mercado ganado/mes</span>
                          <span className={styles.valor}>{formatLocal(convertCLP(mercadoGanado))}</span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Mercado ganado/año</span>
                          <span className={styles.valorDestacado}>{formatLocal(convertCLP(mercadoGanado * 12))}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Delivery;
